/**
 * BooksWantedPage - Buyers post requests for books they're looking for
 * Sellers get notified when matching items are listed
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Search, Plus, BookOpen, Clock, MapPin, DollarSign,
  Bell, X, CheckCircle, AlertTriangle, User, Mail, Phone
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'books', label: 'Books' },
  { value: 'magazines', label: 'Magazines' },
  { value: 'educational-media', label: 'Educational Media' },
  { value: 'comics-graphic-novels', label: 'Comics & Graphic Novels' },
  { value: 'cds-dvds', label: 'CDs & DVDs' },
  { value: 'collectibles', label: 'Collectibles' },
  { value: 'stationery', label: 'Stationery' }
];

const CONDITIONS = [
  { value: 'new', label: 'New' },
  { value: 'like_new', label: 'Like New' },
  { value: 'good', label: 'Good' },
  { value: 'used', label: 'Used' },
  { value: 'worn', label: 'Worn' }
];

// Wanted Request Card
const WantedCard = ({ request, currencySymbol, onContact }) => {
  const daysLeft = Math.max(0, Math.ceil((new Date(request.expires_at) - new Date()) / (1000 * 60 * 60 * 24)));
  
  return (
    <Card className="hover:shadow-lg transition-all" data-testid={`wanted-card-${request.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3 className="font-semibold text-lg">{request.title}</h3>
            {request.author && (
              <p className="text-sm text-gray-500">by {request.author}</p>
            )}
          </div>
          <Badge variant={daysLeft > 7 ? "secondary" : "destructive"}>
            {daysLeft} days left
          </Badge>
        </div>
        
        <div className="mt-3 flex flex-wrap gap-2">
          <Badge variant="outline">{CATEGORIES.find(c => c.value === request.category)?.label}</Badge>
          {request.condition_acceptable?.map(cond => (
            <Badge key={cond} variant="secondary" className="text-xs">
              {CONDITIONS.find(c => c.value === cond)?.label}
            </Badge>
          ))}
        </div>
        
        {request.description && (
          <p className="text-sm text-gray-600 mt-3 line-clamp-2">{request.description}</p>
        )}
        
        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <DollarSign className="w-4 h-4" />
              Budget: {currencySymbol}{request.max_budget?.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {request.location}
            </span>
          </div>
          
          <Button size="sm" onClick={() => onContact(request)}>
            <Bell className="w-4 h-4 mr-1" />
            I Have This
          </Button>
        </div>
        
        {request.matches_found > 0 && (
          <div className="mt-3 p-2 bg-green-50 rounded-lg">
            <p className="text-sm text-green-700 flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              {request.matches_found} potential match(es) found
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function BooksWantedPage() {
  const navigate = useNavigate();
  const { country } = useCountry();
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    isbn: '',
    category: 'books',
    condition_acceptable: ['new', 'like_new', 'good'],
    max_budget: '',
    description: '',
    buyer_email: '',
    buyer_name: '',
    buyer_phone: '',
    location: '',
    expires_in_days: 30
  });
  const [submitting, setSubmitting] = useState(false);
  
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  
  // Fetch wanted requests
  useEffect(() => {
    fetchRequests();
  }, [country, categoryFilter]);
  
  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ country });
      if (categoryFilter) params.append('category', categoryFilter);
      
      const response = await fetch(`${API_URL}/api/books-media/wanted?${params}`);
      const data = await response.json();
      setRequests(data.requests || []);
    } catch (err) {
      console.error('Failed to fetch requests:', err);
      toast.error('Failed to load wanted requests');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.max_budget || !formData.buyer_email || !formData.buyer_name || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/books-media/wanted?country=${country}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          max_budget: parseFloat(formData.max_budget)
        })
      });
      
      if (!response.ok) throw new Error('Failed to create request');
      
      toast.success('Wanted request created! You\'ll be notified when matches are found.');
      setShowCreateModal(false);
      setFormData({
        title: '',
        author: '',
        isbn: '',
        category: 'books',
        condition_acceptable: ['new', 'like_new', 'good'],
        max_budget: '',
        description: '',
        buyer_email: '',
        buyer_name: '',
        buyer_phone: '',
        location: '',
        expires_in_days: 30
      });
      fetchRequests();
    } catch (err) {
      toast.error('Failed to create request');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleContact = (request) => {
    setSelectedRequest(request);
    setShowContactModal(true);
  };
  
  const handleConditionToggle = (condition) => {
    setFormData(prev => ({
      ...prev,
      condition_acceptable: prev.condition_acceptable.includes(condition)
        ? prev.condition_acceptable.filter(c => c !== condition)
        : [...prev.condition_acceptable, condition]
    }));
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Search className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Books Wanted</h1>
              <p className="text-white/80">Looking for something specific? Post a request and sellers will find you!</p>
            </div>
          </div>
          
          <div className="flex gap-4 mt-6">
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-white text-orange-600 hover:bg-gray-100"
              size="lg"
            >
              <Plus className="w-5 h-5 mr-2" />
              Post a Wanted Request
            </Button>
            <Button 
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              size="lg"
              onClick={() => navigate('/books-media')}
            >
              <BookOpen className="w-5 h-5 mr-2" />
              Browse Listings
            </Button>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <p className="text-gray-600">
              <span className="font-semibold">{requests.length}</span> active requests
            </p>
          </div>
        </div>
        
        {/* Requests Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i}>
                <CardContent className="p-5 space-y-3">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : requests.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Search className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No wanted requests yet</h3>
              <p className="text-gray-500 mb-6">Be the first to post what you're looking for!</p>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Post a Wanted Request
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {requests.map(request => (
              <WantedCard
                key={request.id}
                request={request}
                currencySymbol={currencySymbol}
                onContact={handleContact}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Create Request Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Post a Wanted Request</DialogTitle>
            <DialogDescription>
              Tell sellers what you're looking for. You'll be notified when matching items are listed.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>What are you looking for? *</Label>
                <Input
                  placeholder="e.g., Harry Potter and the Philosopher's Stone - First Edition"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Author / Artist</Label>
                <Input
                  placeholder="e.g., J.K. Rowling"
                  value={formData.author}
                  onChange={(e) => setFormData({...formData, author: e.target.value})}
                />
              </div>
              
              <div>
                <Label>ISBN (if known)</Label>
                <Input
                  placeholder="e.g., 978-0747532743"
                  value={formData.isbn}
                  onChange={(e) => setFormData({...formData, isbn: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Category *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Maximum Budget ({currencySymbol}) *</Label>
                <Input
                  type="number"
                  placeholder="e.g., 5000"
                  value={formData.max_budget}
                  onChange={(e) => setFormData({...formData, max_budget: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label>Acceptable Conditions</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {CONDITIONS.map(cond => (
                  <div key={cond.value} className="flex items-center gap-2">
                    <Checkbox
                      id={`cond-${cond.value}`}
                      checked={formData.condition_acceptable.includes(cond.value)}
                      onCheckedChange={() => handleConditionToggle(cond.value)}
                    />
                    <Label htmlFor={`cond-${cond.value}`} className="font-normal">{cond.label}</Label>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Additional Details</Label>
              <Textarea
                placeholder="Any specific edition, features, or requirements..."
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Your Contact Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Your Name *</Label>
                  <Input
                    value={formData.buyer_name}
                    onChange={(e) => setFormData({...formData, buyer_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.buyer_email}
                    onChange={(e) => setFormData({...formData, buyer_email: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.buyer_phone}
                    onChange={(e) => setFormData({...formData, buyer_phone: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Location *</Label>
                  <Input
                    placeholder="e.g., Colombo, London"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                  />
                </div>
              </div>
            </div>
            
            <Alert>
              <Bell className="w-4 h-4" />
              <AlertDescription>
                Your request will be active for {formData.expires_in_days} days. You'll receive email notifications when matching items are listed.
              </AlertDescription>
            </Alert>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Posting...' : 'Post Request'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Contact Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Contact Buyer</DialogTitle>
            <DialogDescription>
              Let {selectedRequest?.buyer_name} know you have what they're looking for!
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium">{selectedRequest.title}</h4>
                <p className="text-sm text-gray-500">Budget: {currencySymbol}{selectedRequest.max_budget?.toLocaleString()}</p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span>{selectedRequest.buyer_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <a href={`mailto:${selectedRequest.buyer_email}`} className="text-blue-600 hover:underline">
                    {selectedRequest.buyer_email}
                  </a>
                </div>
                {selectedRequest.buyer_phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <a href={`tel:${selectedRequest.buyer_phone}`} className="text-blue-600 hover:underline">
                      {selectedRequest.buyer_phone}
                    </a>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{selectedRequest.location}</span>
                </div>
              </div>
              
              <Alert>
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription>
                  For your safety, meet in public places and verify items before payment.
                </AlertDescription>
              </Alert>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactModal(false)}>Close</Button>
            <Button onClick={() => navigate('/books-media/post')}>
              <Plus className="w-4 h-4 mr-2" />
              List Your Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
